import {
  changePasswordSchema,
  DEPARTMENTS,
  OFFICE_LOCATIONS,
  profileSchema,
  type NotificationType,
} from '@helpdesk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, CheckCheck, Download, Smartphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useOpenNotification, NotificationItem } from '../components/NotificationBell.tsx';
import { LeaveCard } from '../components/LeaveCard.tsx';
import { TwoFactorCard } from '../components/TwoFactorCard.tsx';
import { Button } from '../components/ui/button.tsx';
import { Field, Input, Select } from '../components/ui/form.tsx';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../components/ui/misc.tsx';
import { useToast } from '../components/ui/toast.tsx';
import { api, ApiError } from '../lib/api.ts';
import { useHomePath } from '../lib/navigation.ts';
import { useMarkRead, useNotificationList } from '../lib/notifications.ts';
import { disablePush, enablePush, useInstall, usePushState, type PushState } from '../lib/pwa.ts';
import { sessionKey, useSession, useSetSessionUser } from '../lib/session.ts';
import type { NotificationSettings, Session, User } from '../lib/types.ts';
import { formValues, validate, type FieldErrors } from '../lib/validate.ts';

// ── Notifications list ──────────────────────────────────────────────

export function NotificationsPage() {
  const list = useNotificationList(50);
  const markRead = useMarkRead();
  const open = useOpenNotification();
  const unread = list.data?.unreadCount ?? 0;
  const home = useHomePath();

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        back={home}
        title="Notifications"
        actions={
          unread > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => markRead.mutate(undefined)}
              loading={markRead.isPending}
            >
              <CheckCheck className="size-4" aria-hidden />
              Mark all read
            </Button>
          )
        }
      />
      {list.isPending && <Spinner />}
      {list.isError && <Alert tone="error">{list.error.message}</Alert>}
      {list.data && (
        <Card className="divide-y divide-line overflow-hidden">
          {list.data.items.length === 0 ? (
            <EmptyState icon={<BellOff className="size-6" />} title="No notifications yet">
              You’ll be notified here when IT replies to your tickets or their status changes.
            </EmptyState>
          ) : (
            list.data.items.map((n) => <NotificationItem key={n.id} n={n} onOpen={open} />)
          )}
        </Card>
      )}
    </div>
  );
}

// ── Settings: profile, notifications, security ──────────────────────

export function SettingsPage() {
  const home = useHomePath();
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader back={home} title="Profile & notifications" />
      <ProfileCard />
      <DeviceCard />
      <NotificationSettingsCard />
      <PasswordCard />
      <TwoFactorCard />
      <LeaveCard />
      <SecurityCard />
    </div>
  );
}

function ProfileCard() {
  const { user } = useSession();
  const client = useQueryClient();
  const toast = useToast();
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<User>('/api/me', body),
    onSuccess: (updated) => {
      client.setQueryData<Session>(sessionKey, (old) => old && { ...old, user: updated });
      toast({ tone: 'success', title: 'Profile saved' });
    },
    onError: (err) =>
      setErrors(err instanceof ApiError ? { ...err.fields, _: err.message } : { _: String(err) }),
  });
  if (!user) return null;

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = formValues(e.currentTarget);
    const result = validate(profileSchema, {
      name: v.name,
      department: v.department || null,
      officeLocation: v.officeLocation || null,
      phone: v.phone || null,
    });
    if (!result.ok) return setErrors(result.fields);
    setErrors({});
    save.mutate(result.data);
  };

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="font-semibold">Profile</h2>
      <p className="mt-0.5 mb-5 text-sm text-muted">
        Used to fill in the ticket form for you. Signed in as {user.email}.
      </p>
      <form onSubmit={submit} noValidate className="space-y-4">
        {errors._ && <Alert tone="error">{errors._}</Alert>}
        <Field label="Full name" error={errors.name} required>
          <Input name="name" defaultValue={user.name} autoComplete="name" maxLength={100} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Department" error={errors.department}>
            <Select
              name="department"
              options={DEPARTMENTS}
              placeholder="—"
              defaultValue={user.department ?? ''}
            />
          </Field>
          <Field label="Office location" error={errors.officeLocation}>
            <Select
              name="officeLocation"
              options={OFFICE_LOCATIONS}
              placeholder="—"
              defaultValue={user.officeLocation ?? ''}
            />
          </Field>
        </div>
        <Field label="Phone" error={errors.phone}>
          <Input
            name="phone"
            type="tel"
            defaultValue={user.phone ?? ''}
            autoComplete="tel"
            maxLength={25}
            className="sm:max-w-xs"
          />
        </Field>
        <Button type="submit" loading={save.isPending}>
          Save profile
        </Button>
      </form>
    </Card>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-40 ${checked ? 'bg-brand' : 'bg-line'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`}
      />
    </button>
  );
}

const PUSH_TEXT: Record<PushState, string> = {
  on: 'This device will show a notification when there’s news on your tickets, even when the app is closed.',
  off: 'Get a notification on this device when IT replies or your ticket changes.',
  denied:
    'Notifications are blocked for this site in your browser settings. Allow them there, then come back to turn them on.',
  unsupported: 'This browser can’t receive push notifications. You’ll still get emails and see updates here.',
  'needs-install':
    'On iPhone and iPad, notifications work once the helpdesk is added to your Home Screen (see above). Open it from there to turn them on.',
  'server-off': 'Push notifications aren’t switched on for the helpdesk yet. You’ll still get emails.',
};

function DeviceCard() {
  const install = useInstall();
  const [push, setPush] = usePushState();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const client = useQueryClient();

  const toggle = async () => {
    setBusy(true);
    try {
      const next = push === 'on' ? await disablePush() : await enablePush();
      setPush(next);
      void client.invalidateQueries({ queryKey: ['notifications', 'settings'] });
      if (next === 'on') toast({ tone: 'success', title: 'Notifications are on for this device' });
    } catch (err) {
      toast({
        tone: 'error',
        title: 'Something went wrong',
        body: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="font-semibold">This device</h2>
      <div className="mt-4 space-y-5">
        <div className="flex flex-wrap items-start gap-3">
          <Smartphone className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden />
          <div className="min-w-48 flex-1">
            <p className="font-medium">App</p>
            <p className="text-sm text-muted">
              {install.installed
                ? 'You’re using the installed helpdesk app.'
                : install.iosManual
                  ? 'Add the helpdesk to your Home Screen: tap the Share button, then “Add to Home Screen”.'
                  : install.canPrompt
                    ? 'Install the helpdesk as an app for quick access from your home screen or desktop.'
                    : 'You can install the helpdesk from your browser’s menu (“Install app” or “Add to Home screen”).'}
            </p>
          </div>
          {install.canPrompt && !install.installed && (
            <Button size="sm" variant="secondary" onClick={() => void install.install()}>
              <Download className="size-4" aria-hidden />
              Install
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <BellRing className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden />
          <div className="min-w-48 flex-1">
            <p className="font-medium">Push notifications</p>
            <p className="text-sm text-muted">{push ? PUSH_TEXT[push] : 'Checking…'}</p>
          </div>
          {(push === 'on' || push === 'off') && (
            <Button
              size="sm"
              variant={push === 'on' ? 'secondary' : 'primary'}
              onClick={toggle}
              loading={busy}
            >
              {push === 'on' ? 'Turn off' : 'Turn on'}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function NotificationSettingsCard() {
  const client = useQueryClient();
  const key = ['notifications', 'settings'];
  const settings = useQuery({
    queryKey: key,
    queryFn: () => api.get<NotificationSettings>('/api/notifications/settings'),
  });
  const save = useMutation({
    mutationFn: (body: Partial<Record<NotificationType, { email?: boolean; push?: boolean }>>) =>
      api.put('/api/notifications/settings', body),
    onMutate: (body) => {
      // Flip the switch immediately; the server confirms in the background.
      client.setQueryData<NotificationSettings>(
        key,
        (old) =>
          old && {
            ...old,
            types: old.types.map((t) => {
              const change = body[t.type];
              return change
                ? { ...t, emailOn: change.email ?? t.emailOn, pushOn: change.push ?? t.pushOn }
                : t;
            }),
          },
      );
    },
    onError: () => void client.invalidateQueries({ queryKey: key }),
  });

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="font-semibold">Notifications</h2>
      <p className="mt-0.5 text-sm text-muted">
        You always see notifications here in the app. Choose what else reaches you
        {settings.data?.pushAvailable ? ' by email or as a push notification.' : ' by email.'}
      </p>
      {settings.isPending && <Spinner />}
      {settings.data && (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted">
                  <th className="pb-2 font-medium">Notify me about</th>
                  <th className="w-16 pb-2 text-center font-medium">Email</th>
                  {settings.data.pushAvailable && <th className="w-16 pb-2 text-center font-medium">Push</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {settings.data.types.map((t) => (
                  <tr key={t.type}>
                    <td className="py-3 pr-3">
                      <span className="block font-medium">{t.label}</span>
                      <span className="block text-muted">{t.description}</span>
                    </td>
                    <td className="py-3 text-center">
                      {t.email ? (
                        <Toggle
                          checked={t.emailOn}
                          label={`Email: ${t.label}`}
                          onChange={(v) => save.mutate({ [t.type]: { email: v } })}
                        />
                      ) : (
                        <span className="text-muted" title="No emails for this">
                          —
                        </span>
                      )}
                    </td>
                    {settings.data.pushAvailable && (
                      <td className="py-3 text-center">
                        <Toggle
                          checked={t.pushOn}
                          label={`Push: ${t.label}`}
                          onChange={(v) => save.mutate({ [t.type]: { push: v } })}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

function PasswordCard() {
  const { isIt } = useSession();
  const setUser = useSetSessionUser();
  const toast = useToast();
  const [errors, setErrors] = useState<FieldErrors>({});
  const change = useMutation({
    mutationFn: (body: object) => api.post('/api/auth/password', body),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Password changed', body: 'Your other devices have been signed out.' });
      setErrors({});
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) setUser(null);
      setErrors(err instanceof ApiError ? { ...err.fields, _: err.message } : { _: String(err) });
    },
  });
  if (!isIt) return null;

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const v = formValues(form);
    const result = validate(changePasswordSchema, v);
    const fields: FieldErrors = result.ok ? {} : result.fields;
    if (v.newPassword !== v.confirm) fields.confirm = 'Passwords do not match.';
    if (!result.ok || fields.confirm) return setErrors(fields);
    change.mutate(result.data, { onSuccess: () => form.reset() });
  };

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="font-semibold">Password</h2>
      <form onSubmit={submit} noValidate className="mt-4 space-y-4">
        {errors._ && <Alert tone="error">{errors._}</Alert>}
        <Field label="Current password" error={errors.currentPassword}>
          <Input name="currentPassword" type="password" autoComplete="current-password" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="New password"
            error={errors.newPassword}
            hint="10+ characters, upper- and lower-case, and a number."
          >
            <Input name="newPassword" type="password" autoComplete="new-password" />
          </Field>
          <Field label="Confirm new password" error={errors.confirm}>
            <Input name="confirm" type="password" autoComplete="new-password" />
          </Field>
        </div>
        <Button type="submit" loading={change.isPending}>
          Change password
        </Button>
      </form>
    </Card>
  );
}

function SecurityCard() {
  const setUser = useSetSessionUser();
  const signOutAll = useMutation({
    mutationFn: () => api.post('/api/auth/logout-all'),
    onSuccess: () => setUser(null),
  });
  return (
    <Card className="p-5 sm:p-6">
      <h2 className="font-semibold">Security</h2>
      <p className="mt-0.5 mb-4 text-sm text-muted">
        Lost a phone, or signed in on a shared computer? Sign out everywhere, including here.
      </p>
      <Button variant="secondary" onClick={() => signOutAll.mutate()} loading={signOutAll.isPending}>
        Sign out on all devices
      </Button>
    </Card>
  );
}
